// The workspace's small menus (.ws-menu: a session's ⋯, Organize, the account, a file's ⋯ in Files).

import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { usePresence } from './ds';

/** A small menu under (or over) its button: the Organize menu's surface. Closes on a click away or Esc (focus goes
 *  back to its button). Near the foot of `within` (the list it sits in) it opens upward when `room` doesn't fit. */
export function useMenu(within = '.ws-sb-scroll', room = 86) {
  const [open, setOpen] = useState(false);
  const [flip, setFlip] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  const p = usePresence(open, 120);
  useEffect(() => {
    if (!open) return;
    // near the foot of the list the menu opens upward instead of being cut off
    const box = ref.current?.closest(within)?.getBoundingClientRect();
    const at = ref.current?.getBoundingClientRect();
    setFlip(!!box && !!at && at.bottom + room > box.bottom && at.top - room > box.top);
    const away = (e: PointerEvent) => !ref.current?.contains(e.target as Node) && setOpen(false);
    const esc = (e: globalThis.KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setOpen(false);
      if (ref.current?.contains(document.activeElement)) ref.current.querySelector<HTMLElement>('[aria-haspopup]')?.focus();
    };
    document.addEventListener('pointerdown', away);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('pointerdown', away);
      document.removeEventListener('keydown', esc);
    };
  }, [open, within, room]);
  // opened by keyboard or mouse alike, the first item has focus so the arrows work at once
  useEffect(() => {
    if (p.shown) ref.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
  }, [p.shown]);
  return { open, setOpen, ref, p, flip };
}

/** Arrow keys, Home and End move between a menu's items. */
export function menuKeys(e: KeyboardEvent<HTMLElement>) {
  const items = [...e.currentTarget.querySelectorAll<HTMLElement>('[role^="menuitem"]')];
  const at = items.indexOf(document.activeElement as HTMLElement);
  const to = e.key === 'ArrowDown' ? (at + 1) % items.length : e.key === 'ArrowUp' ? (at - 1 + items.length) % items.length : e.key === 'Home' ? 0 : e.key === 'End' ? items.length - 1 : -1;
  if (to < 0) return;
  e.preventDefault();
  items[to].focus();
}
